import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DeviceModels, LanguageCode } from '../../config';
import ProductErrorInfo from '../../components/product/ProductErrorInfo';
import ProductInfo from '../../components/product/ProductInfo';
import { connect } from 'react-redux';
import moment from 'moment';
import { ErrorCodeAction, ProductAction } from '../../actions';
import { CommonAction } from '../../actions/CommonAction';
import { ErrorCodeInfo } from '../../components/errorCode'
import { Translate as t } from '../../config/Translate';
import { Loading } from '../../components/common/Loading';

import { AggrErrorCodeLastNDays, AggrErrorByCodeLastNDays, AggrAllErrCount, AggrBeginEndDiff } from '../../config/Aggregates';
import { history } from '../../history';
import ServiceByErrorCode from '../../components/service/ServiceByErrorCode';
import WidgetCustomTable from '../../widgets/WidgetCustomTable';
import WidgetBarChart from '../../widgets/WidgetBarChart';

const AdminErrorInfo = ({ su, product: storedProduct, GetErrorCode, errorCodeInfo: storedErrorCode, GetProduct }) => {

	const { sn, eCode, errId, view } = useParams();
	const [tab, setTab] = useState(view || "info");
	const [isLoading, setIsLoading] = useState(true);
	const [editable, setEditable] = useState(false);
	const [data, setData] = useState({ code: "" });
	// 조회가 실패하면 redux 에 직전 레코드가 남으므로, 이 화면의 대상(에러 이력의 code, 경로의 sn)과 일치할 때만 쓴다.
	// 불일치하면 기존 '없음' 처리(오류 코드 없음 안내)로 떨어진다.
	const errorCodeInfo = (data.code && String(storedErrorCode?.errorCode ?? '').toLowerCase() === String(data.code).toLowerCase())
		? storedErrorCode : null;
	const product = (sn && storedProduct?.sn === sn) ? storedProduct : {};
	const [allClose, setAllClose] = useState({});
	const [beginEnd, setBeginEnd] = useState(0);

	// 에러 코드 조회가 끝나면(성공·실패 모두) 로딩 해제. 조회할 코드가 없으면(sn·errId 없음, 에러 이력 조회 실패) 바로 해제.
	useEffect(() => {
		let alive = true;
		if (data.code) {
			Promise.resolve(GetErrorCode(data.code, "eng"))
				.catch((e) => console.warn('[AdminErrorInfo] GetErrorCode failed', { code: data.code, reason: e?.message || String(e) }))
				.finally(() => { if (alive) setIsLoading(false); });
			getErrCodeExtraInfo();
		}
		return () => { alive = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data])

	useEffect(() => {
		let alive = true;
		if (sn && errId) {
			setIsLoading(true);
			GetProduct(sn);
			getErrCodeBeginEndDiff();
			getErroInfo()
				.then((code) => { if (alive && !code) setIsLoading(false); })
				.catch(() => { if (alive) setIsLoading(false); });
		} else {
			setIsLoading(false);
		}
		return () => { alive = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const getErrCodeBeginEndDiff = async () => {
		const diffInfo = await getData(AggrBeginEndDiff(data.code));
		if (diffInfo.length > 0) {
			setBeginEnd(Math.round(diffInfo[0].periodsum / (diffInfo[0].allcount * 1000)));
		}
	}

	const getErrCodeExtraInfo = async () => {
		const countInfo = await getData(AggrAllErrCount(data.code));
		if (countInfo.length > 0) {
			setAllClose({
				all: countInfo[0].value,
				open: countInfo[0].notCloseCount,
			})
		}
	}

	const getData = async (aggregate) => {
		const extra = await CommonAction.GetCountAction(aggregate);
		return extra.data;
	}

	const getErroInfo = async () => {
		const errorInfo = await ProductAction.GetProductErrorId(sn, errId)
		if (errorInfo) {
			setData({ ...errorInfo });
		}
		return errorInfo?.code;
	}

	const tabList = [
		{ code: "info", title: t('common.label.overview') },
		{ code: "service", title: t('service.title') },
		{ code: "err", title: t('common.label.information') },
	]

	const errorCodePrdheaders = [
		{ name: "_id", label: t('product.field.model') },
		{ name: "value", label: t('common.label.count'), type: "NUMBER" },
		{ name: "", label: "", type: "PROGRESS", value: "value", style: { minWidth: 200 } },
	];

	const errorCodeSNheaders = [
		{ name: "_id", label: t('product.field.sn'), type: "SN" },
		{ name: "value", label: t('common.label.count'), type: "NUMBER" },
		{ name: "", label: "", type: "PROGRESS", value: "value", style: { minWidth: 200 } },
	];

	return (
		<>
			{!isLoading && (
				<>
					{errorCodeInfo &&
						<>
							<div className="extraHeader">
								<h2 className="mb-0">
									{errorCodeInfo.errorCode}
									{<span className="badge badge-primary ms-1">Open : {allClose.open} / All : {allClose.all}</span>}
									{<span className="badge badge-warning ms-1">Begin ~ End Time(Avg.) : {moment.duration(beginEnd, 'second').humanize()}</span>}
								</h2>
								<div className="ray-right">
									<Link to="/Admin/AdminErrorList" className="btn btn-outline-default btn-sm mt-0 me-1">{t('common.label.list')}</Link>
								</div>
							</div>
							<div id="appCapsule" className="extra-header-active full-height">
								<div className="section mt-1 mb-1">
									<ul className="nav nav-tabs style2 mt-1 mb-1">
										{tabList.map(x => {
											return (
												<li className="nav-item" key={x.code}>
													<Link to={"/Admin/AdminErrorInfo/" + eCode + "/" + sn + "/" + errId + "/" + x.code}
														className={tab == x.code ? "nav-link active" : "nav-link"}
														onClick={() => setTab(x.code)}>{x.title}</Link>
												</li>
											)
										})}
									</ul>
								</div>
								{tab == "info" &&
									<div className="section">
										<WidgetBarChart
											title={errorCodeInfo.errorCode + ` ${t('common.label.occurred')} - 1 Months`}
											height={400}
											days={30}
											aggregate={AggrErrorByCodeLastNDays(errorCodeInfo.errorCode, 'isClose', 30)} />
										<div className="row">
											<div className="col ps-1">
												<WidgetCustomTable
													name="wLatestAcqProduct"
													title={errorCodeInfo.errorCode + ` ${t('common.label.byModel')} (1 Months)`}
													headers={errorCodePrdheaders}
													aggregate={AggrErrorCodeLastNDays(errorCodeInfo.errorCode, 'model', 30)} />
											</div>
											<div className="col">
												<WidgetCustomTable
													name="wLatestAcqProduct"
													title={errorCodeInfo.errorCode + ` ${t('common.label.byProduct')} (1 Months)`}
													headers={errorCodeSNheaders}
													aggregate={AggrErrorCodeLastNDays(errorCodeInfo.errorCode, 'sn', 30, 10)} />
											</div>
											<div className="col pe-1" style={{ margin: -8 }}>
												<ProductErrorInfo errorCodeInfo={data} />
												<ProductInfo productInfo={product} editable={false} showsn={true} />
											</div>
										</div>
									</div>}
								{tab == "service" && errorCodeInfo.errorCode && <ServiceByErrorCode errorCode={errorCodeInfo.errorCode} />}
								{tab == "err" && errorCodeInfo.errorCode && <ErrorCodeInfo errorCodeInfo={errorCodeInfo} readonly={true} />}
							</div>
						</>
					}
					{!errorCodeInfo && <div id="appCapsule" className="full-height mt-2">
						<div className="section">
							<div className="alert alert-danger">{t('product.message.errorCodeNotExist')}</div>
							<button className="btn btn-dark" onClick={() => history.goBack()}>{t('common.button.prevPage')}</button>
						</div>
					</div>}
				</>
			)}
			{isLoading && <Loading />}
		</>
	);
};

const mapState = (state) => {
	const su = state.AuthReducer.su;
	const product = state.ProductReducer.product;
	const errorCodeInfo = state.ErrorCodeReducer.errorCode;
	return { su, product, errorCodeInfo };
};

const mapDispatch = (dispatch) => ({
	GetProduct: (sn) => dispatch(ProductAction.GetProduct(sn)),
	GetErrorCode: (errorCode, lang) => dispatch(ErrorCodeAction.GetOne(errorCode, lang)),
});

export default connect(mapState, mapDispatch)(AdminErrorInfo);
